import { BlockOutlined, CloseOutlined, DeleteOutline, DownloadOutlined, EditOutlined, MoreHoriz, SaveOutlined, Visibility } from '@mui/icons-material'
import { IconButton, Menu, MenuItem, Typography } from '@mui/material'
import { FunctionComponent, ReactNode, useState, MouseEvent } from 'react'
import { IconName } from '../../../shared/interfaces/icon-picker.model'
import { IconPicker } from '../display-elements-components/icon-picker'

export const TableActionButtons: FunctionComponent<{
  includeEdit?: boolean,
  includeDelete?: boolean,
  includeView?: boolean,
  viewOptions?: { id: string; label: string; onClick: () => void }[],
  includeSave?: boolean,
  includeDownload?: boolean,
  includeClose?: boolean,
  includeDeactivate?: boolean,
  includeOther?: boolean,
  id: string,
  otherButtonIcon?: IconName,
  otherButtonText?: string,
  onOther?: (...args: any[]) => void,
  onEdit?: (...args: any[]) => void,
  onDelete?: (...args: any[]) => void,
  onView?: (...args: any[]) => void,
  onOpen?: () => void,
  onSave?: (...args: any[]) => void,
  onDownload?: (...args: any[]) => void,
  onDeactivate?: (...args: any[]) => void,
  onClose?: (...args: any[]) => void,
  disableButton?: boolean,
  disableOtherButton?: boolean,
  additionalArgs?: unknown[],
  /** Extra items in the same ⋯ menu (e.g. scope-specific actions). */
  additionalMenuItems?: { label: string; onClick: () => void; disabled?: boolean; icon?: ReactNode }[],
  /**
   * When true: View → Edit → additional items → other → … → Delete last.
   * Default keeps legacy order: Edit → Delete → View → …
   */
  preferViewThenAdditionalThenDelete?: boolean,
}> = ({
  includeDelete = false,
  includeEdit = false,
  includeView = false,
  viewOptions,
  includeSave = false,
  includeDownload = false,
  includeClose = false,
  includeDeactivate = false,
  includeOther = false,
  id,
  otherButtonIcon,
  otherButtonText,
  onOther,
  onDeactivate,
  onEdit,
  onDelete,
  onOpen,
  onView,
  onSave,
  onClose,
  onDownload,
  disableButton = false,
  disableOtherButton = false,
  additionalArgs = [],
  additionalMenuItems = [],
  preferViewThenAdditionalThenDelete = false,
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const openDropdown = Boolean(anchorEl)
    const openDropdownHandler = (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      if (onOpen) {
        onOpen();
      }
      setAnchorEl(event.currentTarget)
    }
    const closeDropdownHandler = () => { setAnchorEl(null) }

    const viewMenu = includeView && (
      Array.isArray(viewOptions) && viewOptions.length > 1
        ? (
          <>
            {viewOptions.map(option => (
              <MenuItem key={option.id} onClick={() => { option.onClick(); closeDropdownHandler() }} disabled={disableButton}>
                <Visibility sx={{ cursor: 'pointer', mr: 1 }} />
                <Typography>{option.label}</Typography>
              </MenuItem>
            ))}
          </>
        )
        : (
          <MenuItem onClick={() => { onView?.(id, ...additionalArgs); closeDropdownHandler() }} disabled={disableButton}>
            <Visibility sx={{ cursor: 'pointer', mr: 1 }} />
            <Typography>View</Typography>
          </MenuItem>
        )
    )

    const editMenuItem = includeEdit && (
      <MenuItem onClick={() => { onEdit?.(id, ...additionalArgs); closeDropdownHandler() }} disabled={disableButton}>
        <EditOutlined sx={{ cursor: 'pointer', mr: 1 }} />
        <Typography>Edit</Typography>
      </MenuItem>
    )

    const deleteMenuItem = includeDelete && (
      <MenuItem onClick={() => { onDelete?.(id, ...additionalArgs); closeDropdownHandler() }} disabled={disableButton}>
        <DeleteOutline sx={{ cursor: 'pointer', mr: 1 }} />
        <Typography>Delete</Typography>
      </MenuItem>
    )

    const additionalMenuItemNodes = additionalMenuItems.map((item) => (
      <MenuItem
        key={item.label}
        onClick={() => { item.onClick(); closeDropdownHandler() }}
        disabled={disableButton || item.disabled}
      >
        {item.icon}
        <Typography>{item.label}</Typography>
      </MenuItem>
    ))

    const saveDownloadCloseDeactivate = (
      <>
        {includeSave && (
          <MenuItem onClick={() => { onSave?.(id, ...additionalArgs); closeDropdownHandler() }} disabled={disableButton}>
            <SaveOutlined sx={{ cursor: 'pointer', mr: 1 }} />
            <Typography>Save</Typography>
          </MenuItem>
        )}
        {includeDownload && (
          <MenuItem onClick={() => { onDownload?.(id, ...additionalArgs); closeDropdownHandler() }} disabled={disableButton}>
            <DownloadOutlined sx={{ cursor: 'pointer', mr: 1 }} />
            <Typography>Download</Typography>
          </MenuItem>
        )}
        {includeClose && (
          <MenuItem onClick={() => { onClose?.(id, ...additionalArgs); closeDropdownHandler() }} disabled={disableButton}>
            <CloseOutlined sx={{ cursor: 'pointer', mr: 1 }} />
            <Typography>Close</Typography>
          </MenuItem>
        )}
        {includeDeactivate && (
          <MenuItem onClick={() => { onDeactivate?.(id, ...additionalArgs); closeDropdownHandler() }} disabled={disableButton}>
            <BlockOutlined
              sx={{ cursor: 'pointer', mr: 1 }} />
            <Typography>Deactivate</Typography>
          </MenuItem>
        )}
      </>
    )

    const otherMenuItem = includeOther && otherButtonIcon && otherButtonText && (
      <MenuItem onClick={() => { onOther?.(id, ...additionalArgs); closeDropdownHandler() }} disabled={disableButton || disableOtherButton}>
        <IconPicker icon={otherButtonIcon} />
        <Typography sx={{ ml: 1 }}>{otherButtonText}</Typography>
      </MenuItem>
    )

    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <span onClick={(e) => e.stopPropagation()}>
        <IconButton
          aria-controls={openDropdown ? 'table-action-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={openDropdown ? 'true' : undefined}
          onClick={openDropdownHandler}
        >
          <MoreHoriz />
        </IconButton>
        <Menu
          id="table-action-menu"
          anchorEl={anchorEl}
          open={openDropdown}
          onClose={closeDropdownHandler}
          MenuListProps={{
            'aria-labelledby': 'table-action-button',
          }}
        >
          {preferViewThenAdditionalThenDelete ? (
            <>
              {viewMenu}
              {editMenuItem}
              {additionalMenuItemNodes}
              {otherMenuItem}
              {saveDownloadCloseDeactivate}
              {deleteMenuItem}
            </>
          ) : (
            <>
              {editMenuItem}
              {deleteMenuItem}
              {viewMenu}
              {saveDownloadCloseDeactivate}
              {otherMenuItem}
              {additionalMenuItemNodes}
            </>
          )}
        </Menu>
      </span>
    )
  }